import React from "react";
import "./criterion-card.scss";

export type CriterionStatus = "met" | "not-met";
export type ConfidenceColor = "low" | "medium" | "high" | "neutral";

export interface CriterionCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  variant?: "default" | "root";
  id?: string;
  className?: string;
  text: string;
  status: CriterionStatus;
  textClassName?: string;
  textStyle?: React.CSSProperties;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  children?: React.ReactNode;

  statusBadge?: React.ReactNode;
  documentIcon?: React.ReactNode;
  thumbsUpIcon?: React.ReactNode;
  thumbsDownIcon?: React.ReactNode;
  thumbsDownIconFilled?: React.ReactNode;

  showDocumentCount?: boolean;
  documentCount?: number;
  documentLabel?: string;
  onDocumentClick?: () => void;
  documentCountAriaLabel?: string;

  showConfidence?: boolean;
  confidence?: number;
  confidenceLabel?: string;
  confidenceColor?: ConfidenceColor;

  showAiFeedback?: boolean;
  aiFeedbackLabel?: string;
  onThumbsUp?: () => void;
  onThumbsDown?: () => void;
  thumbsUpActive?: boolean;
  thumbsDownActive?: boolean;
  thumbsUpDisabled?: boolean;
  thumbsDownDisabled?: boolean;
}

const CONFIDENCE_COLOR_MODIFIERS: Record<ConfidenceColor, string> = {
  low: "criterion-card__confidence-value--low",
  medium: "criterion-card__confidence-value--medium",
  high: "criterion-card__confidence-value--high",
  neutral: "criterion-card__confidence-value--neutral",
};

const CriterionCard = ({
  variant = "default",
  id,
  className,
  text,
  status,
  textClassName,
  textStyle,
  contentClassName,
  contentStyle,
  children,
  statusBadge,
  documentIcon,
  thumbsUpIcon,
  thumbsDownIcon,
  thumbsDownIconFilled,
  showDocumentCount,
  documentCount,
  documentLabel,
  onDocumentClick,
  documentCountAriaLabel,
  showConfidence,
  confidence,
  confidenceLabel,
  confidenceColor: confidenceColorProp,
  showAiFeedback,
  aiFeedbackLabel,
  onThumbsUp,
  onThumbsDown,
  thumbsUpActive,
  thumbsDownActive,
  thumbsUpDisabled,
  thumbsDownDisabled,
  ...rest
}: CriterionCardProps) => {
  const confidenceColor = confidenceColorProp ?? "neutral";
  const confidenceValueModifier = CONFIDENCE_COLOR_MODIFIERS[confidenceColor];

  const thumbsDownIconToShow =
    thumbsDownActive === true && thumbsDownIconFilled != null
      ? thumbsDownIconFilled
      : thumbsDownIcon;

  const showDocBlock =
    showDocumentCount === true &&
    documentCount != null &&
    documentLabel != null;
  const showConfBlock =
    showConfidence === true && confidence != null && confidenceLabel != null;
  const hasAiActions = onThumbsUp != null || onThumbsDown != null;
  const showAiBlock = showAiFeedback === true && hasAiActions;

  const showFooter = showDocBlock || showConfBlock || showAiBlock;
  const titleId = id ? `${id}-title` : undefined;

  const documentCountAria =
    documentCountAriaLabel != null
      ? documentCountAriaLabel
      : showDocBlock
        ? `${documentCount} ${documentLabel}`
        : undefined;

  return (
    <article
      id={id}
      className={[
        "criterion-card",
        variant === "root" && "criterion-card--root",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby={titleId}
      {...rest}
    >
      {variant === "root" ? (
        <svg
          className="criterion-card__root-line"
          xmlns="http://www.w3.org/2000/svg"
          width="1"
          height="680"
          viewBox="0 0 1 680"
          fill="none"
          preserveAspectRatio="none"
          aria-hidden
        >
          <path
            d="M0.5 0V680"
            stroke="var(--Primary-Gray-14, #D6D6D6)"
            strokeWidth={1}
          />
        </svg>
      ) : null}
      <div className="criterion-card__header">
        <div
          id={titleId}
          className={["criterion-card__text", textClassName].filter(Boolean).join(" ")}
          style={textStyle}
        >
          {text}
        </div>
        {statusBadge}
      </div>

      {children != null ? (
        <div
          className={["criterion-card__content", contentClassName].filter(Boolean).join(" ")}
          style={contentStyle}
        >
          {children}
        </div>
      ) : null}

      {showFooter && (
        <div className="criterion-card__footer">
          {showDocBlock && (
            <div className="criterion-card__doc">
              {onDocumentClick != null ? (
                <button
                  type="button"
                  onClick={onDocumentClick}
                  className="criterion-card__doc-button"
                  aria-label={documentCountAria}
                >
                  {documentIcon}
                  <span>
                    {documentCount} {documentLabel}
                  </span>
                </button>
              ) : (
                <>
                  {documentIcon}
                  <span
                    className="criterion-card__doc-count"
                    aria-label={documentCountAria}
                  >
                    {documentCount} {documentLabel}
                  </span>
                </>
              )}
            </div>
          )}
          <div className="criterion-card__footer-right">
            {showConfBlock && (
              <div className="criterion-card__confidence">
                {confidenceLabel}{" "}
                <span
                  className={["criterion-card__confidence-value", confidenceValueModifier].join(" ")}
                  aria-label={`${confidence} percent`}
                >
                  {confidence}%
                </span>
              </div>
            )}
            {showAiBlock && (
              <div
                className="criterion-card__ai-feedback"
                role="group"
                aria-label={aiFeedbackLabel}
              >
                {aiFeedbackLabel != null && (
                  <span className="criterion-card__ai-label">
                    {aiFeedbackLabel}
                  </span>
                )}
                {onThumbsUp != null && (
                  <button
                    type="button"
                    onClick={onThumbsUp}
                    disabled={thumbsUpDisabled === true}
                    className={[
                      "criterion-card__thumbs-btn",
                      thumbsUpActive === true ? "criterion-card__thumbs-btn--up-active" : "",
                    ].filter(Boolean).join(" ")}
                    aria-label="Mark AI assessment as helpful"
                    aria-pressed={thumbsUpActive === true}
                  >
                    {thumbsUpIcon}
                  </button>
                )}
                {onThumbsDown != null && (
                  <button
                    type="button"
                    onClick={onThumbsDown}
                    disabled={thumbsDownDisabled === true}
                    className={[
                      "criterion-card__thumbs-btn",
                      thumbsDownActive === true ? "criterion-card__thumbs-btn--down-active" : "",
                    ].filter(Boolean).join(" ")}
                    aria-label="Mark AI assessment as unhelpful"
                    aria-pressed={thumbsDownActive === true}
                  >
                    {thumbsDownIconToShow}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

export { CriterionCard };
export default CriterionCard;
